// ---------------------------------------------------------------------------
// THE GALLERY'S OWN URLs, PARSED WITHOUT A ROUTER.
//
// This exists because of a constraint that took three attempts to find, and it
// is worth writing down so nobody spends them again:
//
//   1. A <MemoryRouter> holding the cell's URL, nested inside the app's
//      <BrowserRouter>, is REFUSED. React Router allows exactly one Router in a
//      tree.
//   2. <Routes location={url}> avoids the second Router — and is refused too,
//      because a nested <Routes> resolves relative to its parent match. An
//      absolute "/console/clients" underneath a "/demo/*" route is not a path
//      React Router will accept.
//
// A gallery cell must render a screen at a URL that has nothing to do with the
// browser's, because the screen reads :id out of it. The only arrangement that
// permits that is for the cell's MemoryRouter to be THE router — which means
// the gallery cannot sit inside App's BrowserRouter at all. So main.tsx mounts
// it as a separate root, and the gallery does its own tiny bit of path parsing
// here.
//
// That is a better arrangement than the one it replaces, for a reason beyond
// making it work: the gallery is now not routed by the product's router, so a
// production build has no route to it even if the module leaked. The build flag
// is checked in main.tsx before anything is mounted.
//
// Pure, so it is asserted in web/test/demo rather than by clicking.
// ---------------------------------------------------------------------------

export const DEMO_ROOT = "/demo";

export type DemoRoute =
  | { kind: "index" }
  | { kind: "screen"; screen: string; state: string; theme: string }
  | { kind: "condition"; condition: string }
  | { kind: "unknown"; path: string };

/**
 * `/demo`                                   the index
 * `/demo/screen/{screen}/{state}`           a cell, at the surface's own theme
 * `/demo/screen/{screen}/{state}/{theme}`   a cell, in one named theme
 * `/demo/condition/{condition}`             a session-guard outcome
 *
 * An unrecognised path is `unknown` and NOT the index. Silently falling back to
 * the index would make a typo in a link look like a working link to the wrong
 * place, and the gallery is the one artefact whose job is to make gaps visible.
 */
export function parseDemoPath(pathname: string): DemoRoute {
  const trimmed = pathname.replace(/\/+$/, "");
  if (trimmed === DEMO_ROOT || trimmed === "") return { kind: "index" };
  if (!trimmed.startsWith(DEMO_ROOT + "/")) return { kind: "unknown", path: pathname };

  const parts = trimmed.slice(DEMO_ROOT.length + 1).split("/").filter(Boolean);
  if (parts[0] === "screen" && parts.length >= 3) {
    return {
      kind: "screen",
      screen: decodeURIComponent(parts[1]!),
      state: decodeURIComponent(parts[2]!),
      // The surface's own default when none is named. `light` is not a default
      // here — themeFor decides it — but a cell URL that named no theme used to
      // silently mean light, which made every dark console cell reachable only
      // by typing the word.
      theme: parts[3] ? decodeURIComponent(parts[3]) : "",
    };
  }
  if (parts[0] === "condition" && parts.length >= 2) {
    return { kind: "condition", condition: decodeURIComponent(parts[1]!) };
  }
  return { kind: "unknown", path: pathname };
}

export function cellPath(screen: string, state: string, theme: string): string {
  return `${DEMO_ROOT}/screen/${screen}/${state}/${theme}`;
}

export function conditionPath(condition: string): string {
  return `${DEMO_ROOT}/condition/${condition}`;
}

/** True when this page load should mount the gallery rather than the product. */
export function isDemoPath(pathname: string): boolean {
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === DEMO_ROOT || trimmed.startsWith(DEMO_ROOT + "/");
}
